import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { formatDate } from '../utils/dateUtils';
import { buildApiUrl } from '../config/environment';

const CreditModal = ({ show, onClose, credit, tenancyLedgerId, onSave }) => {
  const { agencyCurrency, user, agencyCountry, agencyId } = useContext(AuthContext);
  const currencySymbol = getCurrencySymbol(agencyCurrency);

  const formatDateWithContext = (dateString) =>
    formatDate(dateString, { userPreference: user?.dateFormatPreference, agencyCountry });

  const [formData, setFormData] = useState({
    amount: '',
    credit_type_id: '',
    reason: '',
    issued_at: new Date().toISOString().split('T')[0]
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Dynamic credit types from backend
  const [creditTypes, setCreditTypes] = useState([]);
  const [loadingCreditTypes, setLoadingCreditTypes] = useState(false);

  // Fetch credit types from API when modal opens
  useEffect(() => {
    if (!show || !agencyId) return;

    const fetchCreditTypes = async () => {
      setLoadingCreditTypes(true);
      try {
        const token = sessionStorage.getItem('access_token') ||
                     sessionStorage.getItem('token') ||
                     localStorage.getItem('access_token') ||
                     localStorage.getItem('token');

        const response = await fetch(
          buildApiUrl(`/agencies/${agencyId}/credit-types`),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setCreditTypes(data || []);
        } else {
          console.error('[CreditModal] Failed to fetch credit types:', response.status);
          setCreditTypes([]);
        }
      } catch (error) {
        console.error('[CreditModal] Error fetching credit types:', error);
        setCreditTypes([]);
      } finally {
        setLoadingCreditTypes(false);
      }
    };

    fetchCreditTypes();
  }, [show, agencyId]);

  // Populate form when editing an existing credit
  useEffect(() => {
    if (credit) {
      const creditTypeId = credit.credit_type_id || (credit.credit_type?.id) || '';
      setFormData({
        amount: credit.amount?.amount ? (credit.amount.amount / 100) : (credit.amount || ''),
        credit_type_id: creditTypeId,
        reason: credit.reason || '',
        issued_at: credit.issued_at ? credit.issued_at.split('T')[0] : new Date().toISOString().split('T')[0]
      });
    } else {
      setFormData({
        amount: '',
        credit_type_id: '',
        reason: '',
        issued_at: new Date().toISOString().split('T')[0]
      });
    }
    setErrors({});
  }, [credit, show]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be a positive number.';
    }
    if (!formData.credit_type_id) {
      newErrors.credit_type_id = 'Credit type is required.';
    }
    if (!formData.reason || formData.reason.trim() === '') {
      newErrors.reason = 'Reason is required.';
    }
    if (!formData.issued_at) {
      newErrors.issued_at = 'Date is required.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      const token = sessionStorage.getItem('access_token') ||
                   sessionStorage.getItem('token') ||
                   localStorage.getItem('access_token') ||
                   localStorage.getItem('token');

      if (!token) throw new Error('Authentication token not found');

      const amountInCents = Math.round(parseFloat(formData.amount) * 100);
      const payload = {
        amount: {
          amount: amountInCents,
          currency: agencyCurrency || 'AUD'
        },
        credit_type_id: parseInt(formData.credit_type_id, 10),
        reason: formData.reason.trim(),
        issued_at: new Date(formData.issued_at).toISOString()
      };

      const url = buildApiUrl(`/tenancy-ledgers/${tenancyLedgerId}/credits`);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to save credit' }));
        throw new Error(error.message || `HTTP ${response.status}: Failed to save credit`);
      }

      const result = await response.json();
      onSave(result);
    } catch (error) {
      console.error('[CreditModal] Error saving credit:', error);
      setErrors(prev => ({ ...prev, submit: error.message }));
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header bg-info text-white">
            <h5 className="modal-title">
              <i className="bi bi-credit-card me-2"></i>
              {credit ? 'Edit Credit' : 'Add Credit'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {errors.submit && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {errors.submit}
                </div>
              )}

              <div className="row g-4">
                {/* Amount */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Amount <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">{currencySymbol}</span>
                    <input
                      type="number"
                      className={`form-control form-control-lg ${errors.amount ? 'is-invalid' : ''}`}
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      required
                    />
                    {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                  </div>
                </div>

                {/* Credit Type */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Credit Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select form-select-lg ${errors.credit_type_id ? 'is-invalid' : ''}`}
                    name="credit_type_id"
                    value={formData.credit_type_id}
                    onChange={handleInputChange}
                    required
                    disabled={loadingCreditTypes}
                  >
                    <option value="">
                      {loadingCreditTypes ? 'Loading types...' : 'Select credit type...'}
                    </option>
                    {creditTypes.map(ct => (
                      <option key={ct.id} value={ct.id}>
                        {ct.display_name}
                      </option>
                    ))}
                  </select>
                  {errors.credit_type_id && <div className="invalid-feedback">{errors.credit_type_id}</div>}
                </div>

                {/* Date */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${errors.issued_at ? 'is-invalid' : ''}`}
                    name="issued_at"
                    value={formData.issued_at}
                    onChange={handleInputChange}
                    onKeyDown={(e) => e.preventDefault()}
                    required
                  />
                  {formData.issued_at && (
                    <small className="form-text fw-semibold text-primary">
                      {formatDateWithContext(formData.issued_at)}
                    </small>
                  )}
                  {errors.issued_at && <div className="invalid-feedback">{errors.issued_at}</div>}
                </div>

                {/* Reason */}
                <div className="col-12">
                  <label className="form-label fw-semibold">
                    Reason <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="e.g. Air conditioning unit was broken for 2 weeks — credit issued as compensation"
                    required
                  />
                  {errors.reason && <div className="invalid-feedback">{errors.reason}</div>}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-info text-white" disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    {credit ? 'Update Credit' : 'Add Credit'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreditModal;

